/**
 * OGraf v1 graphics (0.93.0): the second graphics engine beside renderAt templates.
 *
 * An OGraf package (made in PRISM Graphics, or by any OGraf tool) is a .zip with one `*.ograf.json` manifest and an
 * ES module whose default export is a custom element with the OGraf Graphics API (load, setActionsSchedule,
 * goToTime, dispose…). PRISM renders it non-real time: every picture is `goToTime(timestamp)` on a schedule of
 * actions made from the clip on the timeline. The preview and the renderer use the same host page
 * (buildOgrafHost) and the same functions below, so they agree frame for frame.
 *
 * Everything here is pure: frame arithmetic, the action schedule, the default clip length, the poster time, the
 * form the inspector generates from the manifest's schema, and the summary PRISM keeps as the template manifest.
 *
 * Time: every timestamp is computed from an integer frame index at the SEQUENCE rate, with one division per value
 * (`n × 1000 × den / num` ms), so nothing drifts over long clips at 29.97 fps.
 */
import { TICKS_PER_SECOND, snapFrameRate, ticksPerFrame, type Rational } from './time.ts';
import type { TemplateManifest } from './graphics.ts';
import { aspectLabel, sameAspect } from './framing.ts';

export const OGRAF_ENGINE = 'ograf-v1' as const;
export const RENDERAT_ENGINE = 'renderat' as const;
export type GraphicEngine = typeof RENDERAT_ENGINE | typeof OGRAF_ENGINE;

/** A field value in a sequence's graphic instance: strings for renderAt; strings, numbers and booleans for OGraf. */
export type GraphicValue = string | number | boolean;
export type GraphicData = Record<string, GraphicValue>;

export interface OgrafActionDuration {
  type: string;
  duration?: number;
  steps?: Array<{ step?: number; duration: number }>;
  customActionId?: string;
}

/** One property of the manifest's `schema` (a GDD JSON Schema). */
export interface OgrafProperty {
  type?: string | string[];
  title?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  maxLength?: number;
  minLength?: number;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  hidden?: boolean;
  v_ui?: string;
  gddType?: string;
  [key: string]: unknown;
}

/** The parts of an OGraf v1 manifest PRISM reads (validated against the vendored JSON Schema at ingest). */
export interface OgrafManifest {
  $schema?: string;
  id: string;
  version?: string;
  name: string;
  description?: string;
  main: string;
  supportsRealTime: boolean;
  supportsNonRealTime: boolean;
  stepCount?: number;
  schema?: { type?: string; properties?: Record<string, OgrafProperty>; required?: string[]; [key: string]: unknown };
  actionDurations?: OgrafActionDuration[];
  renderRequirements?: Array<{
    resolution?: { width?: { ideal?: number }; height?: { ideal?: number } };
    frameRate?: { ideal?: number; exact?: number; min?: number; max?: number };
    /** 1.7.0 (with PRISM Graphics 1.2.0): which layout of a multi-layout package this entry is; [0] is the primary. */
    v_layout?: { id?: string; label?: string };
  }>;
  customActions?: Array<{ id: string; name?: string; description?: string; schema?: unknown }>;
  thumbnails?: Array<{ file: string; resolution?: { width?: number; height?: number } }>;
  v_editor?: { frameRate?: Rational; scene?: string; editorVersion?: string; sceneHash?: string; [key: string]: unknown };
  [key: string]: unknown;
}

/** A file of an unpacked package, as the API lists it. */
export interface OgrafFileInfo {
  path: string;
  size: number;
  /** The real type (the app's origin always serves application/octet-stream; see the files endpoint). */
  mime: string;
  sha256: string;
}

const MIME: Record<string, string> = {
  mjs: 'text/javascript', js: 'text/javascript', json: 'application/json', css: 'text/css', txt: 'text/plain', html: 'text/html',
  woff2: 'font/woff2', woff: 'font/woff', ttf: 'font/ttf', otf: 'font/otf',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', avif: 'image/avif', svg: 'image/svg+xml',
  webm: 'video/webm', mp4: 'video/mp4', mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg',
};

/** The type of a package file from its extension. */
export function ografMimeType(path: string): string {
  return MIME[path.split('.').pop()?.toLowerCase() ?? ''] ?? 'application/octet-stream';
}

/* ---------------------------------------------------------------- frames and time */

/** Milliseconds of frame `n` at `rate`: n × 1000 × den / num, one division (never a sum of frame durations). */
export function ografTimestamp(n: number, rate: Rational): number {
  return (n * 1000 * rate.den) / rate.num;
}

/** The frame index nearest a tick position (Math.round), at `rate` (the sequence's). */
export function ticksToFrameIndex(ticks: number, rate: Rational): number {
  return Math.round(ticks / ticksPerFrame(rate));
}

/** Ceil of a ratio that should often be an integer, without floating-point noise pushing it up by one. */
function ceilRatio(a: number, b: number): number {
  const q = a / b;
  const r = Math.round(q);
  return Math.abs(q - r) < 1e-9 ? r : Math.ceil(q);
}

/* ---------------------------------------------------------------- manifest facts */

/** A duration from actionDurations in ms; 0 when it is missing, -1 (dynamic) or negative. */
export function ografDuration(m: OgrafManifest, type: string): number {
  const d = m.actionDurations?.find((a) => a.type === type)?.duration;
  return typeof d === 'number' && d > 0 ? d : 0;
}

export const ografStepCount = (m: OgrafManifest): number => (typeof m.stepCount === 'number' ? m.stepCount : 1);

/** A sting (stepCount 0): its playAction runs in, hold and out by itself, so its length is fixed. */
export const ografIsSting = (m: OgrafManifest): boolean => ografStepCount(m) === 0;

/** The authored picture size: renderRequirements[0].resolution.*.ideal, else 1920×1080. */
const resolutionValue = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= 16384 ? v : null);

export function ografResolution(m: OgrafManifest): { width: number; height: number } {
  const r = m.renderRequirements?.[0]?.resolution;
  return { width: resolutionValue(r?.width?.ideal) ?? 1920, height: resolutionValue(r?.height?.ideal) ?? 1080 };
}

/** The picture size the graphic declares (renderRequirements[0].resolution ideal, either side), or null when none. */
export function ografDeclaredResolution(m: OgrafManifest): { width: number; height: number } | null {
  const r = m.renderRequirements?.[0]?.resolution;
  return resolutionValue(r?.width?.ideal) || resolutionValue(r?.height?.ideal) ? ografResolution(m) : null;
}

/**
 * The size an OGraf graphic lays out at in a sequence frame (1.0.0-rc.11): its declared size; without one, 1920×1080
 * in a 16:9 frame (as before) and the frame's own size in a frame of another shape, so a graphic that lays itself out
 * to its size fills a 9:16 sequence. A declared size of another shape is fitted into the frame (framing.ts
 * graphicPlacement).
 */
export function ografLayoutSize(m: OgrafManifest, frame: { width: number; height: number }): { width: number; height: number } {
  const { layout } = ografLayoutFor(m, frame);
  return { width: layout.width, height: layout.height };
}

/* ---------------------------------------------------------------- 1.7.0: one layout per aspect */

/**
 * One layout of a package (1.7.0, the contract agreed with PRISM Graphics: docs/dev/formats-1.7.0-build.md): one
 * renderRequirements entry per layout, each with v_layout {id, label}; ids are reduced ratios (16x9, 9x16, 4x5, 1x1),
 * [0] is the primary, one layout per aspect, the same frame rate in every entry; the layout is chosen once per load().
 */
export interface OgrafLayout {
  /** Its renderRequirements index. */
  index: number;
  /** v_layout.id, else the reduced ratio of its size ("16x9"). */
  id: string;
  /** v_layout.label, else the reduced ratio ("16:9"). */
  label: string;
  width: number;
  height: number;
}

const layoutRatioId = (s: { width: number; height: number }) => aspectLabel(s).replace(':', 'x');

/**
 * Every layout a package declares, one per renderRequirements entry (sizes as ografResolution: a side left out is
 * 1920 or 1080). A package without renderRequirements has one layout, [0] at 1920×1080.
 */
export function ografLayouts(m: OgrafManifest): OgrafLayout[] {
  const reqs = m.renderRequirements?.length ? m.renderRequirements : [{}];
  return reqs.map((r, index) => {
    const res = (r as { resolution?: { width?: { ideal?: number }; height?: { ideal?: number } } }).resolution;
    const size = { width: resolutionValue(res?.width?.ideal) ?? 1920, height: resolutionValue(res?.height?.ideal) ?? 1080 };
    const v = (r as { v_layout?: { id?: unknown; label?: unknown } }).v_layout;
    const id = typeof v?.id === 'string' && v.id.trim() ? v.id.trim().slice(0, 64) : layoutRatioId(size);
    const label = typeof v?.label === 'string' && v.label.trim() ? v.label.trim().slice(0, 60) : aspectLabel(size);
    return { index, id, label, ...size };
  });
}

/**
 * The layout a graphic uses in a frame (1.7.0): the one of the frame's aspect (within 0.5 %), else the nearest by the
 * smallest |ln(layout aspect / frame aspect)|, ties to the lower index; `exact` says whether its aspect is the frame's
 * (else it is fitted, framing.ts graphicPlacement). A package that declares no size keeps the rc.11 rule: 1920×1080 in
 * a 16:9 frame and the frame's own size in any other, so it lays itself out to the frame. Single-layout packages pick
 * their one layout, as before 1.7.0.
 */
export function ografLayoutFor(m: OgrafManifest, frame: { width: number; height: number }): { layout: OgrafLayout; exact: boolean } {
  const layouts = ografLayouts(m);
  const declaresSize = (m.renderRequirements ?? []).some((r) => resolutionValue(r?.resolution?.width?.ideal) || resolutionValue(r?.resolution?.height?.ideal));
  if (!declaresSize) {
    const size = Math.abs(frame.width / frame.height - 16 / 9) < 0.01 ? { width: 1920, height: 1080 } : { width: frame.width, height: frame.height };
    const first = layouts[0]!;
    const v = m.renderRequirements?.[0]?.v_layout;
    return { layout: { index: 0, id: v?.id ? first.id : layoutRatioId(size), label: v?.label ? first.label : aspectLabel(size), ...size }, exact: true };
  }
  const exact = layouts.find((l) => sameAspect(l, frame));
  if (exact) return { layout: exact, exact: true };
  const fa = frame.width / frame.height;
  let best = layouts[0]!;
  let bestD = Infinity;
  for (const l of layouts) {
    const d = Math.abs(Math.log(l.width / l.height / fa));
    // A later layout wins only when it is nearer by more than 1e-9: ties (3:4 between 9:16 and 1:1) go to the lower
    // index, as PRISM Graphics' chooseLayout (the contract vectors, packages/contracts/test/ograf-layout-vectors-170.test.ts).
    if (d < bestD - 1e-9) { best = l; bestD = d; }
  }
  return { layout: best, exact: false };
}

/** The rate the graphic was authored at (v_editor.frameRate, else renderRequirements' frame rate), or null. */
export function ografAuthoredRate(m: OgrafManifest): Rational | null {
  const v = m.v_editor?.frameRate;
  if (v && Number.isInteger(v.num) && Number.isInteger(v.den) && v.num > 0 && v.den > 0) return { num: v.num, den: v.den };
  const f = m.renderRequirements?.[0]?.frameRate;
  const fps = f?.ideal ?? f?.exact;
  return typeof fps === 'number' && fps > 0 ? snapFrameRate(fps) : null;
}

export const sameRate = (a: Rational, b: Rational): boolean => a.num * b.den === b.num * a.den;

/**
 * The default length of a new clip in ms: in + hold + out, where in is the playAction duration and out the
 * stopAction duration (missing or -1 count as 0). A sting's length is its playAction duration (in + hold + out).
 */
export function ografDefaultLengthMs(m: OgrafManifest, holdSeconds: number): number {
  const play = ografDuration(m, 'playAction');
  if (ografIsSting(m)) return play > 0 ? play : Math.round(holdSeconds * 1000);
  return play + Math.round(holdSeconds * 1000) + ografDuration(m, 'stopAction');
}

/**
 * The poster's moment in ms: the step-0 hold, i.e. the end of the in animation (the playAction duration for step 0,
 * else its duration; 1000 when missing or -1) plus one frame at `rate`. For a sting, half its length.
 */
export function ografPosterMs(m: OgrafManifest, rate: Rational): number {
  const play = m.actionDurations?.find((a) => a.type === 'playAction');
  if (ografIsSting(m)) {
    const len = typeof play?.duration === 'number' && play.duration > 0 ? play.duration : 2000;
    return len / 2;
  }
  const step0 = play?.steps?.find((s) => s.step === 0)?.duration;
  const d = typeof step0 === 'number' ? step0 : play?.duration;
  return (typeof d === 'number' && d >= 0 ? d : 1000) + ografTimestamp(1, rate);
}

/* ---------------------------------------------------------------- the schedule */

export interface OgrafAction {
  type: 'playAction' | 'updateAction' | 'stopAction';
  params: Record<string, unknown>;
}
export interface OgrafScheduleEntry {
  timestamp: number;
  action: OgrafAction;
}

export interface OgrafClip {
  data: GraphicData;
  stepMarks?: number[];
  updates?: Array<{ atTicks: number; data: GraphicData }>;
  durationTicks: number;
}

/** The clip's length in frames at the sequence rate. */
export const ografClipFrames = (durationTicks: number, rate: Rational): number => Math.max(1, ticksToFrameIndex(durationTicks, rate));

/**
 * The frame (from the clip start) where the stop action goes (0.94.0, the clean last frame): (D − 1) − ceil(stopMs ×
 * num / (1000 × den)), clamped at 0, so the out animation finishes on the clip's last frame (D − 1), which is fully
 * transparent. (0.93.x put it one frame later, at D − ceil(…), so the last frame still showed the end of the out.)
 */
export function ografStopFrame(clipFrames: number, stopMs: number, rate: Rational): number {
  return Math.max(0, clipFrames - 1 - ceilRatio(stopMs * rate.num, 1000 * rate.den));
}

/**
 * The action schedule of one clip (times in ms from the clip start), every time snapped to a frame index first:
 *  - playAction { goto: 0 } at 0;
 *  - playAction { delta: 1 } at each step mark;
 *  - updateAction { data } at each update, with the clip data and every update up to it merged (the full data);
 *  - stopAction {} at (D − 1) − ceil(stopMs × num / (1000 × den)), clamped at 0 (the out finishes on the clip's last
 *    frame, D − 1, which is transparent), except for a sting (stepCount 0), whose playAction runs its own out.
 */
export function ografSchedule(m: OgrafManifest, clip: OgrafClip, rate: Rational): OgrafScheduleEntry[] {
  const D = ografClipFrames(clip.durationTicks, rate);
  const at = (frame: number) => ografTimestamp(frame, rate);
  const out: Array<OgrafScheduleEntry & { frame: number; order: number }> = [{ frame: 0, order: 0, timestamp: 0, action: { type: 'playAction', params: { goto: 0 } } }];
  for (const s of [...(clip.stepMarks ?? [])].sort((a, b) => a - b)) {
    const f = ticksToFrameIndex(s, rate);
    if (f >= D) continue;
    out.push({ frame: f, order: 1, timestamp: at(f), action: { type: 'playAction', params: { delta: 1 } } });
  }
  let merged: GraphicData = { ...clip.data };
  for (const u of [...(clip.updates ?? [])].sort((a, b) => a.atTicks - b.atTicks)) {
    const f = ticksToFrameIndex(u.atTicks, rate);
    merged = { ...merged, ...u.data };
    if (f >= D) continue;
    out.push({ frame: f, order: 2, timestamp: at(f), action: { type: 'updateAction', params: { data: { ...merged } } } });
  }
  if (!ografIsSting(m)) {
    const f = ografStopFrame(D, ografDuration(m, 'stopAction'), rate);
    out.push({ frame: f, order: 3, timestamp: at(f), action: { type: 'stopAction', params: {} } });
  }
  // Stable by frame, then play, steps, updates, stop at the same frame.
  return out
    .map((e, i) => ({ ...e, i }))
    .sort((a, b) => a.frame - b.frame || a.order - b.order || a.i - b.i)
    .map(({ timestamp, action }) => ({ timestamp, action }));
}

/** The data in effect at a moment of the clip: the clip data with every update up to and including `atTicks`. */
export function ografDataAt(clip: OgrafClip, atTicks: number): GraphicData {
  let d: GraphicData = { ...clip.data };
  for (const u of [...(clip.updates ?? [])].sort((a, b) => a.atTicks - b.atTicks)) if (u.atTicks <= atTicks) d = { ...d, ...u.data };
  return d;
}

/* ---------------------------------------------------------------- the inspector's form */

export type OgrafFieldKind = 'text' | 'select' | 'color' | 'image' | 'number' | 'boolean' | 'unsupported';

export interface OgrafField {
  id: string;
  kind: OgrafFieldKind;
  title: string;
  description?: string;
  default: GraphicValue | undefined;
  hidden: boolean;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  integer?: boolean;
  /** select and image: the choices (image: package paths). */
  options?: GraphicValue[];
}

const typeOf = (p: OgrafProperty): string | undefined => (Array.isArray(p.type) ? p.type.find((t) => t !== 'null') : p.type);
const scalar = (v: unknown): v is GraphicValue => typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v)) || typeof v === 'boolean';

/** The form for a manifest's schema, in property order (title as the label, default for new clips). */
export function ografFields(m: OgrafManifest): OgrafField[] {
  return Object.entries(m.schema?.properties ?? {}).map(([id, p]) => {
    const t = typeOf(p);
    const base = { id, title: typeof p.title === 'string' && p.title ? p.title : id, ...(typeof p.description === 'string' ? { description: p.description } : {}), hidden: p.hidden === true };
    const def = scalar(p.default) ? p.default : undefined;
    const choices = Array.isArray(p.enum) ? p.enum.filter(scalar) : undefined;
    if (t === 'string') {
      const isColor = p.v_ui === 'color' || p.gddType === 'color-rrggbb';
      if (isColor) return { ...base, kind: 'color' as const, default: typeof def === 'string' ? def.toLowerCase() : '#ffffff' };
      if (p.v_ui === 'image' && choices?.length) return { ...base, kind: 'image' as const, default: def ?? choices[0], options: choices };
      if (choices?.length) return { ...base, kind: 'select' as const, default: def ?? choices[0], options: choices };
      return { ...base, kind: 'text' as const, default: def ?? '', ...(typeof p.maxLength === 'number' ? { maxLength: p.maxLength } : {}) };
    }
    if (t === 'number' || t === 'integer') {
      return {
        ...base, kind: 'number' as const, default: typeof def === 'number' ? def : typeof p.minimum === 'number' ? p.minimum : 0, integer: t === 'integer',
        ...(typeof p.minimum === 'number' ? { minimum: p.minimum } : {}), ...(typeof p.maximum === 'number' ? { maximum: p.maximum } : {}),
      };
    }
    if (t === 'boolean') return { ...base, kind: 'boolean' as const, default: typeof def === 'boolean' ? def : false };
    return { ...base, kind: 'unsupported' as const, default: undefined };
  });
}

/** The data a new clip starts with: every editable field's default. */
export function ografDefaults(m: OgrafManifest): GraphicData {
  const out: GraphicData = {};
  for (const f of ografFields(m)) if (f.kind !== 'unsupported' && f.default !== undefined) out[f.id] = f.default;
  return out;
}

/** The clip's label on the timeline: the values of the fields not marked hidden (text, choices and numbers). */
export function ografLabel(m: OgrafManifest, data: GraphicData): string {
  const parts = ografFields(m)
    .filter((f) => !f.hidden && (f.kind === 'text' || f.kind === 'select' || f.kind === 'number'))
    .map((f) => data[f.id] ?? f.default)
    .filter((v) => v !== undefined && String(v).trim() !== '')
    .map(String);
  return parts.length ? parts.join(' · ') : m.name;
}

/** Whether a value fits a field (the server validates fully with the manifest's JSON Schema). */
export function ografValueFits(f: OgrafField, v: unknown): v is GraphicValue {
  switch (f.kind) {
    case 'text': return typeof v === 'string' && (f.maxLength === undefined || v.length <= f.maxLength);
    case 'color': return typeof v === 'string' && /^#[0-9a-f]{6}$/.test(v);
    case 'select': case 'image': return f.options!.includes(v as GraphicValue);
    case 'number': return typeof v === 'number' && Number.isFinite(v) && (!f.integer || Number.isInteger(v)) && (f.minimum === undefined || v >= f.minimum) && (f.maximum === undefined || v <= f.maximum);
    case 'boolean': return typeof v === 'boolean';
    default: return false;
  }
}

/**
 * Data carried to another version of a graphic ("Update to vN"): values of fields that still exist and still fit
 * are kept; other fields get the new version's defaults; fields that are gone are dropped.
 */
export function ografCarryData(next: OgrafManifest, data: GraphicData): GraphicData {
  const out: GraphicData = {};
  for (const f of ografFields(next)) {
    if (f.kind === 'unsupported') continue;
    if (f.id in data && ografValueFits(f, data[f.id])) out[f.id] = data[f.id]!;
    else if (f.default !== undefined) out[f.id] = f.default;
  }
  return out;
}

/** Partial update data carried to another version: only fields that still exist and fit. */
export function ografCarryPartial(next: OgrafManifest, data: GraphicData): GraphicData {
  const fields = new Map(ografFields(next).map((f) => [f.id, f]));
  return Object.fromEntries(Object.entries(data).filter(([k, v]) => { const f = fields.get(k); return f && ografValueFits(f, v); }));
}

/* ---------------------------------------------------------------- the summary kept as the template manifest */

/**
 * The renderAt-shaped summary PRISM keeps in graphic_templates.manifest for an OGraf version, so code that lists
 * templates keeps working: size, default length, in/out, fields (as text or colour, with string defaults) and the
 * poster moment. The OGraf manifest itself is kept beside it (ograf_manifest).
 */
export function ografSummary(m: OgrafManifest, opts: { templateId: string; version: number; holdSeconds: number }): TemplateManifest {
  const { width, height } = ografResolution(m);
  const rate = ografAuthoredRate(m) ?? { num: 25, den: 1 };
  return {
    id: opts.templateId, version: opts.version, name: m.name, description: m.description ?? '', kind: 'overlay', width, height,
    safeArea: { x: 0, y: 0, width, height },
    defaultDurationSeconds: Math.max(0.04, ografDefaultLengthMs(m, opts.holdSeconds) / 1000),
    animation: { inSeconds: ografDuration(m, 'playAction') / 1000, outSeconds: ografIsSting(m) ? 0 : ografDuration(m, 'stopAction') / 1000 },
    fields: ografFields(m).filter((f) => f.kind !== 'unsupported').map((f) => ({
      id: f.id, label: f.title, type: f.kind === 'color' ? 'color' as const : 'text' as const, default: f.default === undefined ? '' : String(f.default),
      ...(f.maxLength ? { maxLength: f.maxLength } : {}), required: false,
    })),
    fonts: [], entry: m.main, previewTimeSeconds: ografPosterMs(m, rate) / 1000,
  };
}

/** Ticks of a length in ms, snapped to whole frames at `rate`. */
export function msToFrameTicks(ms: number, rate: Rational): number {
  // Frames = ms × num / (1000 × den), rounded (half up), computed in one division so 5100 ms at 25 fps is 127.5 → 128.
  const frames = Math.max(1, Math.round((ms * rate.num) / (1000 * rate.den)));
  return Math.round(frames * ticksPerFrame(rate));
}

/** Renders data for a renderAt template: its values are strings (save validation guarantees it for renderAt). */
export function renderAtData(data: GraphicData): Record<string, string> {
  return Object.fromEntries(Object.entries(data).map(([k, v]) => [k, typeof v === 'string' ? v : String(v)]));
}

/* ---------------------------------------------------------------- stacking (0.93.1) */

export type GraphicLayer = { kind: 'renderat'; index: number } | { kind: 'ograf'; indexes: number[] };

/**
 * How graphics stack, bottom first: strictly in drawing order (pass graphicOrder(doc): 1.7.3 GFX 1 to 3, then the order
 * of `doc.graphics`), the order the export
 * composes its overlays in (each later graphic over the ones before it). The browser runs OGraf graphics in shared
 * host frames and each renderAt template in a frame of its own, so OGraf graphics are grouped into one layer per
 * contiguous run of OGraf graphics in that order: a renderAt graphic between two OGraf graphics splits them, and
 * every layer keeps its place in the stack. Runs are taken over the whole track (not only what is on screen), so
 * the layers do not change while the playhead moves.
 */
export function graphicLayers<T>(graphics: T[], isOgraf: (g: T) => boolean): GraphicLayer[] {
  const out: GraphicLayer[] = [];
  graphics.forEach((g, index) => {
    if (!isOgraf(g)) { out.push({ kind: 'renderat', index }); return; }
    const last = out.at(-1);
    if (last?.kind === 'ograf') last.indexes.push(index);
    else out.push({ kind: 'ograf', indexes: [index] });
  });
  return out;
}
